import React from 'react'
import useReactRouter from 'use-react-router'
import { makeStyles } from '@material-ui/styles'

import DocumentMeta from 'core/components/DocumentMeta'
import NoContentMessage from 'core/components/NoContentMessage'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import usePluginRouter from 'core/hooks/usePluginRouter'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import RbacChecklist from 'k8s/components/access-control/RbacChecklist'
import NamespacePicklist from 'k8s/components/common/NamespacePicklist'
import { K8sPluginGlobalPrefs } from 'k8s/model'
import { createRole } from 'k8s/components/access-control/roles/actions'

const useStyles = makeStyles<Theme>((theme) => ({}))

const defaultParams = {
  clusterId: '', // Initialize here to prevent desync with formContext
  namespace: '',
  rbac: {},
}

export default function AddRolePage() {
  const classes = useStyles({})
  const { history } = useReactRouter()
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences<K8sPluginGlobalPrefs>(
    'k8sPluginGlobalParams',
  )

  const clusterId = k8sPluginGlobalPerfs?.clusterId || ''
  const clusterName = k8sPluginGlobalPerfs?.cluster || ''

  const { currentPluginId } = usePluginRouter()

  const {
    update: create,
    updating: creatingRole,
    reset,
    error: errorCreatingRole,
  } = useUpdateAction(createRole)

  const submitForm = async (params) => {
    const { success } = await create(params)
    if (success) handleClose()
  }

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(
      routeManager.routes.kubernetes.accessControl.roles.path({
        plugin: currentPluginId,
        cluster: clusterName,
      }),
    )
  }

  return (
    <>
      <DocumentMeta title="Add Role" bodyClasses={['form-view']} />
      <ModalForm
        route={routeManager.routes.kubernetes.accessControl.addRoles}
        title="Add Role"
        onSubmit={submitForm}
        onClose={handleClose}
        submitting={creatingRole}
        error={errorCreatingRole}
        submitTitle="Add Role"
        data-testid="add-role-form"
      >
        <>
          <FormFieldSection title="Basic Details" step={1} data-testid="basic-details">
            <TextField id="name" label="Name" required data-testid="name-field" />
            <PicklistField
              DropdownComponent={NamespacePicklist}
              id="namespace"
              label="Namespace"
              clusterId={clusterId}
              onChange={getParamsUpdater('namespace')}
              value={params.namespace}
              setInitialNamespace
              compact={false}
              showAll={false}
              required
              data-testid="namespace-field"
            />
          </FormFieldSection>
          <FormFieldSection title="API Access / Permissions" step={2} data-testid="api-access">
            <RbacChecklist
              id="rbac"
              clusterId={clusterId}
              onChange={getParamsUpdater('rbac')}
              value={params.rbac}
              data-testid="rbac-checklist"
            />
            {!clusterId && (
              <NoContentMessage message="Please choose a cluster to define it's API access permissions." />
            )}
          </FormFieldSection>
        </>
      </ModalForm>
    </>
  )
}
